import React, { Reducer } from 'react';
import { ParkPayload } from 'services/payloads/ParkPayload';
import { Quote } from 'models/Quote';
import { Reservation } from 'models/Reservation';
import { SiteTypePayload } from 'services/payloads/SiteTypePayload';
import { Guest } from 'models/Guest';
import { Session } from './Session';

type State = {
  selectedPark: ParkPayload;
  quote: Quote;
  siteType: SiteTypePayload;
  reservation: Reservation;
  guest: Guest;
};

type Action<T, P = never> = {
  type: T;
  payload?: P;
};

type SelectParkAction = Action<'SELECT_PARK', { park: ParkPayload }>;
type SetQuoteAction = Action<'SET_QUOTE', { quote: Quote }>;
type SetSiteTypeAction = Action<'SET_SITE_TYPE', { siteType: SiteTypePayload }>;
type SetReservationAction = Action<'SET_RESERVATION', { reservation: Reservation }>;
type SetGuestAction = Action<'SET_GUEST', { guest: Guest }>;

type Actions =
 | SelectParkAction
 | SetQuoteAction
 | SetSiteTypeAction
 | SetReservationAction
 | SetGuestAction

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'SELECT_PARK':
      Session.parkId = action.payload.park.id;
      return {
        ...state,
        selectedPark: action.payload.park,
      };
    case 'SET_QUOTE':
      return {
        ...state,
        quote: action.payload.quote,
      }
    case 'SET_SITE_TYPE':
      return {
        ...state,
        siteType: action.payload.siteType,
      }
    case 'SET_RESERVATION': {
      const json = Reservation.toJSON(action.payload.reservation);
      window.sessionStorage.setItem('reservation', json);
      return {
        ...state,
        reservation: action.payload.reservation,
      }
    }
    case 'SET_GUEST': {
      const json = JSON.stringify(action.payload.guest);
      window.sessionStorage.setItem('guest', json);
      return {
        ...state,
        guest: action.payload.guest,
      }
    }
    default:
      throw new Error();
  }
}

const GlobalContext = React.createContext<{
  state: State;
  dispatch: (action: Actions) => void;
}>(undefined);

const GlobalContextProvider: React.FC = ({ children }) => {
  const json = window.sessionStorage.getItem('guest');
  const guest = json ? JSON.parse(json) : null;
  const resrvJson = window.sessionStorage.getItem('reservation');
  const reservation = resrvJson ? Reservation.fromJSON(resrvJson) : null;
  
  const initialState = React.useRef<State>({
    selectedPark: null,
    quote: null,
    reservation: reservation,
    siteType: null,
    guest: guest,
  }).current;

  const [state, dispatch] = React.useReducer<Reducer<State, Actions>>(reducer, initialState);

  const contextValue = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => [React.useContext(GlobalContext)];

export const withGlobalContext = (Component: React.FC) => (props: any) => (
  <GlobalContextProvider>
    <Component {...props} />
  </GlobalContextProvider>
);
